import { useState, useEffect } from "react";
import supabase from "../lib/supabaseClient";

export interface UsageInfo {
  used: number;
  limit: number;
  remaining: number;
  percentUsed: number;
  canUse: boolean;
  inTrial: boolean;
  trialEndsAt: string | null;
  planTier: "free" | "basic" | "pro";
}

export interface UsageSummary {
  insights: UsageInfo;
  emails: UsageInfo;
}

export function useUsage() {
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsage = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error("No active session");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "https://api.sendupdates.ai"}/api/usage/summary`,
        {
          headers: {
            "Authorization": `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch usage: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        setUsage(result.data);
      } else {
        throw new Error(result.error || "Failed to fetch usage");
      }
    } catch (err) {
      console.error("Error fetching usage:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsage();
  }, []);

  return {
    usage,
    loading,
    error,
    refetch: fetchUsage,
  };
}

